import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd';
import jaJP from 'antd/locale/ja_JP';
import { AppRouter } from './Router';
import { THEME_COLORS, BASE_COLORS, FEEDBACK_BACKGROUND_COLORS } from '@/shared/constants/colors';
import { FONT_SIZES, LINE_HEIGHTS, FONT_WEIGHTS } from '@/shared/constants/typography';
import { BORDER_RADIUS, SPACING } from '@/shared/constants/spacing';

const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: THEME_COLORS.PRIMARY,
    colorSuccess: THEME_COLORS.SUCCESS,
    colorWarning: THEME_COLORS.WARNING,
    colorError: THEME_COLORS.ERROR,
    colorInfo: THEME_COLORS.INFO,
    colorBorder: BASE_COLORS.BORDER,
    colorBorderSecondary: BASE_COLORS.BORDER,
    colorText: BASE_COLORS.TEXT,
    colorTextSecondary: BASE_COLORS.TEXT_SECONDARY,
    colorErrorBg: FEEDBACK_BACKGROUND_COLORS.ERROR_BG,
    fontSize: FONT_SIZES.BASE,
    fontSizeHeading1: FONT_SIZES.XXXL,
    fontSizeHeading2: FONT_SIZES.XXL,
    fontSizeHeading3: FONT_SIZES.XL,
    fontSizeHeading4: FONT_SIZES.LG,
    fontSizeHeading5: FONT_SIZES.MD,
    fontWeightStrong: FONT_WEIGHTS.SEMIBOLD,
    lineHeight: LINE_HEIGHTS.NORMAL,
    lineHeightHeading1: LINE_HEIGHTS.TIGHT,
    lineHeightHeading2: LINE_HEIGHTS.TIGHT,
    lineHeightHeading3: LINE_HEIGHTS.TIGHT,
    lineHeightHeading4: LINE_HEIGHTS.TIGHT,
    lineHeightHeading5: LINE_HEIGHTS.TIGHT,
    borderRadius: BORDER_RADIUS.sm,
    borderRadiusLG: BORDER_RADIUS.lg,
    borderRadiusSM: BORDER_RADIUS.xs,
    fontFamily: "'Inter', 'Noto Sans JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  components: {
    Button: {
      borderRadius: BORDER_RADIUS.sm,
      controlHeight: 32,
      paddingContentHorizontal: SPACING.SM,
    },
    Card: {
      borderRadiusLG: BORDER_RADIUS.lg,
      paddingLG: SPACING.LG,
    },
    Input: {
      borderRadius: BORDER_RADIUS.sm,
      controlHeight: 32,
      paddingBlock: SPACING.XXS,
      paddingInline: SPACING.XS,
      fontSize: FONT_SIZES.MOBILE_INPUT_MIN,
    },
    Select: {
      borderRadius: BORDER_RADIUS.sm,
      controlHeight: 32,
      fontSize: FONT_SIZES.MOBILE_INPUT_MIN,
    },
    Table: {
      borderRadius: BORDER_RADIUS.sm,
      padding: SPACING.XXS,
    },
    Modal: {
      borderRadiusLG: BORDER_RADIUS.lg,
    },
    Form: {
      itemMarginBottom: SPACING.SM,
      labelFontSize: FONT_SIZES.BASE,
    },
    DatePicker: {
      borderRadius: BORDER_RADIUS.sm,
      controlHeight: 32,
      fontSize: FONT_SIZES.MOBILE_INPUT_MIN,
    },
    Drawer: {
      borderRadiusLG: BORDER_RADIUS.lg,
    },
    Alert: {
      borderRadiusLG: BORDER_RADIUS.sm,
    },
    Tag: {
      borderRadiusSM: BORDER_RADIUS.xs,
    },
    Tabs: {
      itemColor: BASE_COLORS.TEXT,
      itemSelectedColor: THEME_COLORS.PRIMARY_DARK,
      inkBarColor: THEME_COLORS.PRIMARY_DARK,
    },
    Statistic: {
      colorTextDescription: BASE_COLORS.TEXT,
    },
  },
};

export const App: React.FC = () => {
  return (
    <ConfigProvider locale={jaJP} theme={antdTheme}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ConfigProvider>
  );
};